'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { trackAssetDownload, trackButtonClick } from '@/components/Analytics';
import { LeadCaptureForm } from '@/components/LeadCaptureForm';
import { DECKS } from './decks';
import { LEAD_DOWNLOADS_ID } from '@/lib/scroll-targets';
import { ClaimAccountPrompt } from '@/components/ClaimAccountPrompt';

type AuthState = 'checking' | 'anonymous' | 'authenticated';
interface Slide {
  title: string;
  desc: string;
}
interface FaqItem {
  q: string;
  a: string;
}

const SOURCE = 'pitch-deck-templates';

export function PitchDeckTemplatesContent() {
  const { t } = useTranslation();
  const gateRef = useRef<HTMLDivElement>(null);

  const [authState, setAuthState] = useState<AuthState>('checking');
  // Confirmed-by-email, no account. The templates are static files, so this
  // only decides whether the download buttons are shown rather than the form.
  const [leadVerified, setLeadVerified] = useState(false);
  const [cameFromGate, setCameFromGate] = useState(false);
  const [sessionEmail, setSessionEmail] = useState('');

  const faqRaw = t('pageContent.pitchDeckTemplates.faqItems', { returnObjects: true });
  const faqItems: FaqItem[] = Array.isArray(faqRaw) ? (faqRaw as FaqItem[]) : [];

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCameFromGate(new URLSearchParams(window.location.search).get('auth') === 'required');
    }

    let cancelled = false;

    fetch('/api/auth/check-session', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled) return;

        if (data?.authenticated === true) {
          setAuthState('authenticated');
          setSessionEmail(typeof data.email === 'string' ? data.email : '');
          return;
        }

        setAuthState('anonymous');
        setLeadVerified(data?.leadVerified === true);
      })
      .catch(() => {
        if (!cancelled) {
          setAuthState('anonymous');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToGate = () => {
    gateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onDownload = (deckId: string) => {
    try {
      // file_download only. The lead conversion fires once, on the confirmation
      // landing (components/LeadConfirmed) — firing it here as well would
      // double-count every confirmed visitor, and would report three times for
      // anyone who downloads all three templates.
      trackAssetDownload(deckId, SOURCE, true);
    } catch {
      /* non-fatal */
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="container py-16 md:py-24 text-center">
          <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            {t('pageContent.pitchDeckTemplates.badge')}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 max-w-4xl mx-auto leading-tight">
            {t('pageContent.pitchDeckTemplates.hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mt-6 max-w-3xl mx-auto">
            {t('pageContent.pitchDeckTemplates.hero.subtitle')}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <span>✓ {t('pageContent.pitchDeckTemplates.hero.point1')}</span>
            <span>✓ {t('pageContent.pitchDeckTemplates.hero.point2')}</span>
            <span>✓ {t('pageContent.pitchDeckTemplates.hero.point3')}</span>
          </div>
          <div className="mt-8">
            <button
              onClick={scrollToGate}
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-lg transition-colors"
            >
              {t('pageContent.pitchDeckTemplates.hero.cta')}
            </button>
          </div>
        </div>
      </section>

      <section className="container py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4">
          {t('pageContent.pitchDeckTemplates.decks.heading')}
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
          {t('pageContent.pitchDeckTemplates.decks.subheading')}
        </p>

        <div className="space-y-16">
          {DECKS.map((deck) => {
            const base = `pageContent.pitchDeckTemplates.decks.items.${deck.id}`;
            const name = t(`${base}.name`);
            const audience = t(`${base}.audience`);
            const description = t(`${base}.description`);
            const bestFor = t(`${base}.bestFor`, { returnObjects: true }) as string[];
            const slides = t(`${base}.slides`, { returnObjects: true }) as Slide[];
            const slidesList = Array.isArray(slides) ? slides : [];
            const bestForList = Array.isArray(bestFor) ? bestFor : [];
            const downloadLabel = t('pageContent.pitchDeckTemplates.card.download');
            const gateLabel = t('pageContent.pitchDeckTemplates.card.getFree');

            return (
              <article
                key={deck.id}
                id={deck.id}
                className="scroll-mt-24 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start rounded-2xl border border-gray-100 shadow-sm p-6 md:p-10"
              >
                <div>
                  <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={deck.coverPreview}
                      alt={`${name} — cover slide preview`}
                      width={900}
                      height={506}
                      loading="lazy"
                      className="w-full h-auto block"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {Array.from({ length: deck.slideCount }, (_, i) => i + 1)
                      .slice(1)
                      .map((n) => (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          key={n}
                          src={`${deck.previewPrefix}${n}.webp`}
                          alt={`${name} — slide ${n}`}
                          width={200}
                          height={112}
                          loading="lazy"
                          className="w-full h-auto rounded-md border border-gray-200"
                        />
                      ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: deck.accent }}
                      aria-hidden
                    />
                    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                      {audience}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{name}</h3>
                  <p className="text-gray-600 mt-3">{description}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {bestForList.map((b) => (
                      <span key={b} className="text-xs font-medium bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                        {b}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6">
                    <p className="text-sm font-semibold text-gray-900 mb-3">
                      {deck.slideCount} {t('pageContent.pitchDeckTemplates.card.slidesLabel')}
                    </p>
                    <ol className="space-y-2">
                      {slidesList.map((s, i) => (
                        <li key={`${deck.id}-${i}`} className="flex gap-3 text-sm">
                          <span className="text-gray-400 font-mono w-6 flex-shrink-0">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span>
                            <span className="font-semibold text-gray-900">{s.title}</span>
                            <span className="text-gray-600"> — {s.desc}</span>
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="mt-8">
                    {authState === 'authenticated' || leadVerified ? (
                      <a
                        href={deck.file}
                        onClick={() => onDownload(deck.id)}
                        className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                        aria-label={`${downloadLabel}: ${name} (.pptx)`}
                        download
                      >
                        {downloadLabel} (.pptx)
                      </a>
                    ) : (
                      <button
                        onClick={scrollToGate}
                        className="inline-flex items-center gap-2 border border-primary-600 text-primary-600 hover:bg-primary-50 font-semibold px-6 py-3 rounded-lg transition-colors"
                        aria-label={`${gateLabel}: ${name}`}
                      >
                        {gateLabel}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-gray-50 border-y border-gray-100">
        <div ref={gateRef} id={LEAD_DOWNLOADS_ID} className="container py-16 md:py-20 scroll-mt-24">
          <div className="max-w-xl mx-auto">
            {cameFromGate && authState === 'anonymous' && (
              <div
                role="status"
                className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              >
                {t('pageContent.pitchDeckTemplates.form.disclaimer')}
              </div>
            )}

            {authState === 'checking' ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
                <p className="text-gray-500 py-8">{t('pageContent.pitchDeckTemplates.form.submitLoading')}</p>
              </div>
            ) : authState === 'authenticated' || leadVerified ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-green-100">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {t('pageContent.pitchDeckTemplates.form.successTitle')}
                </h2>
                <p className="text-gray-600 mb-6">
                  {sessionEmail
                    ? t('pageContent.pitchDeckTemplates.form.successBody', { email: sessionEmail })
                    : t('pageContent.pitchDeckTemplates.form.confirmedBody')}
                </p>
                <div className="space-y-3">
                  {DECKS.map((deck) => (
                    <a
                      key={deck.id}
                      href={deck.file}
                      onClick={() => onDownload(deck.id)}
                      download
                      className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                    >
                      {t(`pageContent.pitchDeckTemplates.decks.items.${deck.id}.name`)} (.pptx)
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              /* ANONYMOUS: email capture, not a sign-in wall.
                 This branch used to send visitors to /login. That made the only
                 conversion event on the site unreachable without an account —
                 Google Ads reported "Conversion has never received data" for the
                 whole life of the account — and it asked a stranger to create an
                 account before seeing anything. An email form gives them the
                 files immediately and by email, and fires the conversion.
                 Signed-in visitors keep the direct buttons above. */
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">
                  {t('pageContent.pitchDeckTemplates.form.title')}
                </h2>
                <p className="text-gray-600 mt-3 max-w-lg mx-auto text-center mb-6">
                  {t('pageContent.pitchDeckTemplates.form.subtitle')}
                </p>
                <LeadCaptureForm
                  source="pitch-deck-templates"
                  cta={t('pageContent.leadCapture.pitchDeckTemplates.cta', {
                    defaultValue: 'Send me the templates',
                  })}
                  blurb={t('pageContent.leadCapture.pitchDeckTemplates.blurb', {
                    defaultValue:
                      'Three editable .pptx decks — pre-seed, seed B2B SaaS, and AI/deeptech. No account needed.',
                  })}
                />
              </div>
            )}

            <ClaimAccountPrompt
              show={authState === 'anonymous' && leadVerified}
              source="pitch-deck-templates"
            />
          </div>
        </div>
      </section>

      <section className="container py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
          {t('pageContent.pitchDeckTemplates.faq.heading')}
        </h2>
        <div className="max-w-3xl mx-auto divide-y divide-gray-100">
          {faqItems.map((item, i) => (
            <div key={i} className="py-6">
              <h3 className="text-lg font-semibold text-gray-900">{item.q}</h3>
              <p className="text-gray-600 mt-2">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container py-16 md:py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">
            {t('pageContent.pitchDeckTemplates.cta.title')}
          </h2>
          <p className="text-primary-100 mt-4 max-w-2xl mx-auto text-lg">
            {t('pageContent.pitchDeckTemplates.cta.subtitle')}
          </p>
          <div className="mt-8">
            {/* Attribution rides on the click event, not the href. This link
                used to carry ?utm_source=lead_magnet&utm_medium=deck_templates,
                which existed only so GA4's automatic page_view would label the
                visit — nothing in the app reads utm_* from the URL. Internal
                UTMs are a net loss: they mint a second crawlable /how-it-works
                URL and restart the GA session, wiping the original acquisition
                source. A first-party button_click event keeps the lead_magnet →
                deck_templates provenance while the href stays canonical. */}
            <Link
              href="/how-it-works"
              className="inline-block bg-white text-primary-600 font-semibold px-8 py-3.5 rounded-lg hover:bg-primary-50 transition-colors"
              onClick={() => trackButtonClick('lead_magnet_deck_templates_how_it_works_cta')}
            >
              {t('pageContent.pitchDeckTemplates.cta.button')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
